import { _ as _assert_this_initialized } from "@swc/helpers/_/_assert_this_initialized";
import { _ as _class_call_check } from "@swc/helpers/_/_class_call_check";
import { _ as _create_class } from "@swc/helpers/_/_create_class";
import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _inherits } from "@swc/helpers/_/_inherits";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _create_super } from "@swc/helpers/_/_create_super";
import * as React from "react";
import { useEffect, useRef } from "react";
import hoistNonReactStatics from "hoist-non-react-statics";
import { isObservable, isObservableProp, runInAction } from "mobx";
import isEqual from "react-fast-compare";
import { expr } from "./helpers/expr";
import { observer_ } from "./helpers/observer";
import { createStackContext } from "./helpers/createStackContext";
import { initExtends } from "./helpers/initExtends";
import { makeExpressionObservable, isPureFunction, isExpression } from "./helpers/makeExpressionObservable";
import { mapReduce } from "./helpers/mapReduce";
import { makeDisplayName } from "./helpers/makeDisplayName";
// 继续渲染的执行标记
var RENDER_CONTINUE = Symbol.for("RENDER_CONTINUE");
// 标记组件属性回退到PROPS访问的标记
// 由于mobx::observable() 无法处理React.Element格式
// 此处需要明确的标记限定:
//  假如: props.value 值是一个 JSSlot的实例,
//  如果忽略处理, 那么在 `$$vm.value` 的值就是undefined
//  $$vm.$pickit() 过程将会强制使用 `undefined` 来渲染组件
//  此过程并不符合预期
//  为了针对上述情况:
//    将此类型值做统一标记 `FALLBACK_AT_PROPS`
//    然后在`$pickit`过程中做回溯, 即可完整规避此问题的产生
var FALLBACK_AT_PROPS = Symbol.for("FALLBACK_AT_PROPS");
var ALREADY_BOUND_KEYS_CACHE = new WeakMap();
// 生命周期方法名称
var LIFECYCLE_KEYS = [
    "getDerivedStateFromProps",
    "componentWillUnmount",
    "componentDidUpdate",
    "componentDidMount",
    "componentDidCatch",
    "getSnapshotBeforeUpdate"
];
var _createStackContext = createStackContext(), StackContextProvider = _createStackContext.StackContextProvider, useStackContext = _createStackContext.useStackContext;
var isInnerKey = function(key) {
    return key.startsWith("$") || key.startsWith("_") || !!~LIFECYCLE_KEYS.indexOf(key);
};
function usePropsKeys(props) {
    var prevPropsRef = useRef(null);
    // 如果是普通组件, 并没有优化$$vm实例, 以下的计算则与组件无关
    // 并且返回 [validKeys, updatingKeys] 事实上也无需计算
    // 直接返回 即可
    if (!props.$$vm) return [
        null,
        null
    ];
    var keys = Object.keys(props);
    var inners = keys.filter(function(key) {
        return isInnerKey(key) || isPureFunction(props[key]) || !!~[
            "children",
            "style"
        ].indexOf(key);
    });
    var validKeys = keys.filter(function(key) {
        return !~inners.indexOf(key);
    });
    // 首次绑定
    // 很奇怪,  ALREADY_BOUND_KEYS_CACHE.has(props.$$vm) 有值的情况下 prevPropsRef.current 也有可能是null
    //    尚不明确是什么个情况, 怀疑与 react.setState 调用有关.
    if (!ALREADY_BOUND_KEYS_CACHE.has(props.$$vm) || !prevPropsRef.current) {
        ALREADY_BOUND_KEYS_CACHE.set(props.$$vm, new Set(validKeys));
        prevPropsRef.current = props;
        return [
            validKeys,
            validKeys
        ];
    }
    var alreadyBoundKeys = ALREADY_BOUND_KEYS_CACHE.get(props.$$vm);
    // 非首次绑定
    var updatingKeys = validKeys.filter(function(key) {
        if (isExpression(props[key]) || isObservable(props[key]) || isObservableProp(props, key)) return true;
        if (!alreadyBoundKeys.has(key)) {
            alreadyBoundKeys.add(key);
            return true;
        }
        return !isEqual(props[key], prevPropsRef.current[key]);
    });
    prevPropsRef.current = props;
    return [
        validKeys,
        updatingKeys
    ];
}
function useRelateMeta(props, $$type, $$auto_unbox) {
    var _props_$$vm_$meta;
    if (!props.$$vm) return;
    var contexts = useStackContext();
    runInAction(function() {
        var _contexts_, _props_$$vm_$parent_$children, _props_$$vm_$parent;
        var _contexts__$$vm;
        props.$$vm.$parent = (_contexts__$$vm = contexts === null || contexts === void 0 ? void 0 : (_contexts_ = contexts[contexts.length - 1]) === null || _contexts_ === void 0 ? void 0 : _contexts_.$$vm) !== null && _contexts__$$vm !== void 0 ? _contexts__$$vm : null;
        (_props_$$vm_$parent = props.$$vm.$parent) === null || _props_$$vm_$parent === void 0 ? void 0 : (_props_$$vm_$parent_$children = _props_$$vm_$parent.$children) === null || _props_$$vm_$parent_$children === void 0 ? void 0 : _props_$$vm_$parent_$children.add(props.$$vm);
    });
    var _type;
    (_type = (_props_$$vm_$meta = props.$$vm.$meta).type) !== null && _type !== void 0 ? _type : _props_$$vm_$meta.type = $$type;
    props.$$vm.$meta.auto_unbox = $$auto_unbox !== null && $$auto_unbox !== void 0 ? $$auto_unbox : false;
    React.useEffect(function() {
        return function() {
            return runInAction(function() {
                var _props_$$vm_$parent_$children, _props_$$vm_$parent;
                return (_props_$$vm_$parent = props.$$vm.$parent) === null || _props_$$vm_$parent === void 0 ? void 0 : (_props_$$vm_$parent_$children = _props_$$vm_$parent.$children) === null || _props_$$vm_$parent_$children === void 0 ? void 0 : _props_$$vm_$parent_$children.delete(props.$$vm);
            });
        };
    }, []);
}
function useSetKeysToMeta($$vm, keys) {
    // 无需依赖数组, 每次调用都设置即可, 此值更新不应该重新驱动组件的渲染
    useEffect(function() {
        if ($$vm) keys.forEach(function(key) {
            return $$vm.$meta.properties.add(key);
        });
    });
}
function getDuplicateProps(props) {
    return mapReduce(Object.keys(props), function(key) {
        return /*#__PURE__*/ React.isValidElement(props[key]) ? FALLBACK_AT_PROPS : props[key];
    });
}
function useLoopCondition(props) {
    if (!props.$$apis) return RENDER_CONTINUE;
    var _props_$$apis = props.$$apis, createLoopVirtualDOM = _props_$$apis.createLoopVirtualDOM, calculateLoopInfo = _props_$$apis.calculateLoopInfo, calculateConditionInfo = _props_$$apis.calculateConditionInfo;
    if (typeof calculateConditionInfo === "function") {
        var $condition = expr(calculateConditionInfo);
        // 直接响应一个 `null` 阻止节点渲染即可
        if ($condition === false) {
            return null;
        }
    }
    // 不是`Loop`环境, 直接返回, 并执行组件的渲染
    if (typeof calculateLoopInfo === "function" && typeof createLoopVirtualDOM === "function") {
        var $loop = expr(calculateLoopInfo);
        if ($loop) {
            return createLoopVirtualDOM($loop);
        }
    }
    return RENDER_CONTINUE;
}
function WithLLCWrapper(Component) {
    // LifeCycle & Loop & Condition
    var LLC = /*#__PURE__*/ function(_React_Component) {
        "use strict";
        _inherits(LLC, _React_Component);
        var _super = _create_super(LLC);
        function LLC() {
            _class_call_check(this, LLC);
            var _this;
            _this = _super.apply(this, arguments);
            _define_property(_assert_this_initialized(_this), "state", {
                error: false,
                errorInfo: null
            });
            return _this;
        }
        _create_class(LLC, [
            {
                key: "componentDidMount",
                value: function componentDidMount() {
                    var _this_props_$$apis_triggerLifeCycleEvent, _this_props_$$apis_triggerLifeCycleEvent1, _this_props_$$apis;
                    (_this_props_$$apis = this.props.$$apis) === null || _this_props_$$apis === void 0 ? void 0 : (_this_props_$$apis_triggerLifeCycleEvent1 = _this_props_$$apis.triggerLifeCycleEvent) === null || _this_props_$$apis_triggerLifeCycleEvent1 === void 0 ? void 0 : (_this_props_$$apis_triggerLifeCycleEvent = _this_props_$$apis_triggerLifeCycleEvent1.call(_this_props_$$apis, "componentDidMount")) === null || _this_props_$$apis_triggerLifeCycleEvent === void 0 ? void 0 : _this_props_$$apis_triggerLifeCycleEvent();
                }
            },
            {
                key: "componentWillUnmount",
                value: function componentWillUnmount() {
                    var _this_props_$$apis_triggerLifeCycleEvent, _this_props_$$apis_triggerLifeCycleEvent1, _this_props_$$apis;
                    (_this_props_$$apis = this.props.$$apis) === null || _this_props_$$apis === void 0 ? void 0 : (_this_props_$$apis_triggerLifeCycleEvent1 = _this_props_$$apis.triggerLifeCycleEvent) === null || _this_props_$$apis_triggerLifeCycleEvent1 === void 0 ? void 0 : (_this_props_$$apis_triggerLifeCycleEvent = _this_props_$$apis_triggerLifeCycleEvent1.call(_this_props_$$apis, "componentWillUnmount")) === null || _this_props_$$apis_triggerLifeCycleEvent === void 0 ? void 0 : _this_props_$$apis_triggerLifeCycleEvent();
                }
            },
            {
                key: "componentDidUpdate",
                value: function componentDidUpdate(prevProps, prevState, snapshot) {
                    var _this_props_$$apis_triggerLifeCycleEvent, _this_props_$$apis_triggerLifeCycleEvent1, _this_props_$$apis;
                    (_this_props_$$apis = this.props.$$apis) === null || _this_props_$$apis === void 0 ? void 0 : (_this_props_$$apis_triggerLifeCycleEvent1 = _this_props_$$apis.triggerLifeCycleEvent) === null || _this_props_$$apis_triggerLifeCycleEvent1 === void 0 ? void 0 : (_this_props_$$apis_triggerLifeCycleEvent = _this_props_$$apis_triggerLifeCycleEvent1.call(_this_props_$$apis, "componentDidUpdate")) === null || _this_props_$$apis_triggerLifeCycleEvent === void 0 ? void 0 : _this_props_$$apis_triggerLifeCycleEvent();
                }
            },
            {
                key: "componentDidCatch",
                value: function componentDidCatch(error, errorInfo) {
                    var _this_props_$$apis_triggerLifeCycleEvent, _this_props_$$apis_triggerLifeCycleEvent1, _this_props_$$apis;
                    this.setState({
                        error: error,
                        errorInfo: errorInfo
                    });
                    return (_this_props_$$apis = this.props.$$apis) === null || _this_props_$$apis === void 0 ? void 0 : (_this_props_$$apis_triggerLifeCycleEvent1 = _this_props_$$apis.triggerLifeCycleEvent) === null || _this_props_$$apis_triggerLifeCycleEvent1 === void 0 ? void 0 : (_this_props_$$apis_triggerLifeCycleEvent = _this_props_$$apis_triggerLifeCycleEvent1.call(_this_props_$$apis, "componentDidCatch")) === null || _this_props_$$apis_triggerLifeCycleEvent === void 0 ? void 0 : _this_props_$$apis_triggerLifeCycleEvent();
                }
            },
            {
                key: "getSnapshotBeforeUpdate",
                value: function getSnapshotBeforeUpdate(prevProps, prevState) {
                    var _this_props_$$apis_triggerLifeCycleEvent, _this_props_$$apis_triggerLifeCycleEvent1, _this_props_$$apis;
                    var _this_props_$$apis_triggerLifeCycleEvent2;
                    // getSnapshotBeforeUpdate 必须返回一个值, 此值不能是 `undefined`
                    return (_this_props_$$apis_triggerLifeCycleEvent2 = (_this_props_$$apis = this.props.$$apis) === null || _this_props_$$apis === void 0 ? void 0 : (_this_props_$$apis_triggerLifeCycleEvent1 = _this_props_$$apis.triggerLifeCycleEvent) === null || _this_props_$$apis_triggerLifeCycleEvent1 === void 0 ? void 0 : (_this_props_$$apis_triggerLifeCycleEvent = _this_props_$$apis_triggerLifeCycleEvent1.call(_this_props_$$apis, "getSnapshotBeforeUpdate")) === null || _this_props_$$apis_triggerLifeCycleEvent === void 0 ? void 0 : _this_props_$$apis_triggerLifeCycleEvent()) !== null && _this_props_$$apis_triggerLifeCycleEvent2 !== void 0 ? _this_props_$$apis_triggerLifeCycleEvent2 : null;
                }
            },
            {
                key: "render",
                value: function render() {
                    if (this.state.errorInfo) {
                        console.error("组件渲染异常:\n".concat(Component.displayName, "\n"), this.state);
                        return /*#__PURE__*/ React.createElement("label", {
                            style: {
                                color: "#f00",
                                border: "1px solid #f00"
                            }
                        }, Component.displayName, " 组件渲染异常");
                    }
                    var __LOOP_CONDITION_RESULT__ = useLoopCondition(this.props);
                    if (__LOOP_CONDITION_RESULT__ !== RENDER_CONTINUE) {
                        // 如果是 `LOOP | CONDITION` 环境, 并且没有返回 `RENDER_CONTINUE`
                        // 就直接阻止下面的代码运行即可, 直接返回
                        return __LOOP_CONDITION_RESULT__;
                    }
                    return /*#__PURE__*/ React.createElement(Component, _object_spread_props(_object_spread({}, this.props), {
                        $$apis: undefined
                    }));
                }
            }
        ], [
            {
                key: "getDerivedStateFromProps",
                value: function getDerivedStateFromProps(props) {
                    var _props_$$apis_triggerLifeCycleEvent, _props_$$apis_triggerLifeCycleEvent1, _props_$$apis;
                    (_props_$$apis = props.$$apis) === null || _props_$$apis === void 0 ? void 0 : (_props_$$apis_triggerLifeCycleEvent1 = _props_$$apis.triggerLifeCycleEvent) === null || _props_$$apis_triggerLifeCycleEvent1 === void 0 ? void 0 : (_props_$$apis_triggerLifeCycleEvent = _props_$$apis_triggerLifeCycleEvent1.call(_props_$$apis, "getDerivedStateFromProps")) === null || _props_$$apis_triggerLifeCycleEvent === void 0 ? void 0 : _props_$$apis_triggerLifeCycleEvent();
                    return {};
                }
            }
        ]);
        return LLC;
    }(React.Component);
    return observer_(LLC);
}
function patchVisible2Style(props) {
    var style = props.style, visible = props.visible;
    if (visible === false) {
        style = Object.assign({}, style || {});
        style.display = "none";
        return _object_spread_props(_object_spread({}, props), {
            style: style
        });
    }
    return props;
}
export function WithVMWrapper(Component) {
    var $$TYPE = Component.$$TYPE;
    var $$AUTO_UNBOX = Component.$$AUTO_UNBOX;
    var WithObserver = observer_(Component);
    // 多一层`WithVMDriver`的目的是为了优化性能
    // 如果此处逻辑全部堆积到 `WrapperComponent`中
    // 那相关联的组件的更新会受到重新计算的影响而重新执行
    // 执行的结果没有变化, 但是`执行`本身是昂贵的
    // 所以增加此处的隔离层
    var WithVMDriver = observer_(function(props) {
        var _props_$$vm;
        var $register = React.useCallback(function(properties) {
            var _props_$$vm;
            return (_props_$$vm = props.$$vm) === null || _props_$$vm === void 0 ? void 0 : _props_$$vm.$register(properties);
        }, [
            props.$$vm
        ]);
        var _props_$$vm_$pickit;
        var props_ = patchVisible2Style((_props_$$vm_$pickit = (_props_$$vm = props.$$vm) === null || _props_$$vm === void 0 ? void 0 : _props_$$vm.$pickit(props)) !== null && _props_$$vm_$pickit !== void 0 ? _props_$$vm_$pickit : props);
        return /*#__PURE__*/ React.createElement(WithObserver, _object_spread_props(_object_spread({}, props_), {
            $register: $register,
            $$useVMStack: useStackContext
        }));
    });
    var WithVMWrapper = observer_(function(props) {
        useRelateMeta(props, $$TYPE, $$AUTO_UNBOX);
        var _usePropsKeys = _sliced_to_array(usePropsKeys(props), 2), validKeys = _usePropsKeys[0], updatingKeys = _usePropsKeys[1];
        props = makeExpressionObservable(props, isInnerKey);
        useSetKeysToMeta(props.$$vm, validKeys);
        initExtends(props.$$vm, getDuplicateProps(props), updatingKeys);
        if (!props.$$vm) {
            // 不存在 $$vm 说明是一个普通组件, 在此处直接返回
            // 无需处理相关 `VM` 相关注册逻辑, 并禁用 `$$apis`
            return /*#__PURE__*/ React.createElement(WithVMDriver, _object_spread_props(_object_spread({}, props), {
                $$apis: undefined
            }));
        }
        return /*#__PURE__*/ React.createElement(StackContextProvider, {
            value: {
                $$type: $$TYPE,
                $$vm: props.$$vm
            }
        }, /*#__PURE__*/ React.createElement(WithVMDriver, _object_spread_props(_object_spread({}, props), {
            $$apis: undefined
        })));
    });
    var WithLLC = WithLLCWrapper(WithVMWrapper);
    makeDisplayName(WithObserver, Component, "WithObserver");
    makeDisplayName(WithVMDriver, Component, "WithVMDriver");
    makeDisplayName(WithVMWrapper, Component, "WithVMWrapper");
    return hoistNonReactStatics(WithLLC, Component);
}
